<script setup lang="ts">
import { ZhEmptyState, ZhPageTitle } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api, type Me } from '@/api'
import MyMailPanel from '@/components/MyMailPanel.vue'

/**
 * The signed-in person's own inbox: what arrived at the address an owner gave them in this enterprise, with the
 * commands a mail client has and the buttons their tasks put on it. Built from the session identity alone
 * (`GET /auth/me` says whether they have an address) and the person's own mail route, which they reach whatever
 * their grant, so the screen is one of the deliberately unscoped destinations in `navGrant.ts`, like Account.
 */
const me = ref<Me | null>(null)
const loading = ref(true)
/** `/inbox/:emailId`: one mail on the whole page instead of the list. */
const route = useRoute()
const emailId = computed(() => (typeof route.params['emailId'] === 'string' ? route.params['emailId'] : undefined))

onMounted(async () => {
    try {
        me.value = await api.me()
    } catch {
        me.value = null
    } finally {
        loading.value = false
    }
})
</script>

<template>
	<section class="w-full space-y-4 p-8" data-testid="inbox-view">
		<ZhPageTitle kicker="Mail">
			Inbox
			<template v-if="me?.mailAddress" #meta>{{ me.mailAddress }}</template>
		</ZhPageTitle>
		<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
		<MyMailPanel v-else-if="me?.mailAddress" :email-id="emailId" />
		<ZhEmptyState v-else label="No mail address" data-testid="inbox-no-mailbox">
			You have no mail address in {{ me?.enterprise.name ?? 'this enterprise' }} yet. An owner gives you one on People.
		</ZhEmptyState>
	</section>
</template>
